import { Component, OnInit, Inject, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { CalcularPrecoService } from '../../../../shared/services/calcular-preco.service';
import { ProdutoService } from '../../../../shared/services/produto.service';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, delay, filter, map, takeUntil, tap } from 'rxjs/operators';
import { AppConfirmService } from '../../../../shared/services/app-confirm/app-confirm.service';
import { Produto } from '../../../../shared/interfaces/produto';
import { CookieService } from 'ngx-cookie-service';
import { AppLoaderService } from '../../../../shared/services/app-loader/app-loader.service';
import { Preco } from '../../../../shared/interfaces/preco';
import { JwtAuthService } from '../../../../shared/services/auth/jwt-auth.service';
import { ParametroService } from '../../../../shared/services/parametro.service';
import { Router } from '@angular/router';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-produto-manutencao-preco',
  templateUrl: './produto.manutencao.preco.component.html',
  styleUrls: ['./produto.manutencao.preco.component.scss']
})
export class ProdutoManutencaoPrecoComponent implements OnInit {
  precoForm = new FormGroup({
    custoCompra: new FormControl(0),
    impostoSVenda: new FormControl(0, [Validators.min(0), Validators.max(100)]),
    acrescimoFinanceiro: new FormControl(0, [Validators.min(0), Validators.max(100)]),
    despesaOperacional: new FormControl(0, [Validators.min(0), Validators.max(100)]),
    outros: new FormControl(0, [Validators.min(0), Validators.max(100)]),
    comissao: new FormControl(0, [Validators.min(0), Validators.max(100)]),
    precoVendaInput: new FormControl(0),
    lucrosSPreInput: new FormControl(0),
    lucrosSPrecoCustoInput: new FormControl(0),
    valorPrecoVendaInput: new FormControl(0),
    valorPrecoCustoInput: new FormControl(0),
    indiceInput: new FormControl(0),
    produto: new FormControl(''),
    searchProduto: new FormControl('')
  });

  totalEncargos: number = 0;
  totalCustoVenda: number = 0;
  valorEncargo: number = 0;
  valorCustoVenda: number = 0;
  lucrosSPrecoVenda: number = 0;
  lucrosSPrecoCusto: number = 0;
  valorPrecoVenda: number = 0;
  valorPrecoCusto: number = 0;
  precoVenda: number = 0;
  indiceCalc: number = 0;
  precoVendaAtual: number = 0;
  saldo: number = 0;

  produtos: Produto[] = [];
  filteredProdutos: ReplaySubject<Produto[]> = new ReplaySubject<Produto[]>(1);
  produtoSelected?: Produto;
  searching: boolean = false;
  custoManualEditado: boolean = false;
  private atualizandoCustoProgramaticamente: boolean = false;
  protected _onDestroy = new Subject<void>();

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<ProdutoManutencaoPrecoComponent>,
    private calcularPrecoService: CalcularPrecoService,
    private produtoService: ProdutoService,
    private confirmService: AppConfirmService,
    private cookieService: CookieService,
    private loader: AppLoaderService,
    public jwtAuth: JwtAuthService,
    private parametroService: ParametroService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Verifica se foi chamado como popup
    if (this.data) {
      console.log(
        "ProdutoManutencaoPrecoComponent chamado como popup",
        this.data
      );
      // Se for popup e tiver produto nos dados, pré-seleciona o produto
      if (this.data.produto) {
        // this.produtoSelected = this.data.produto;
        // this.precoForm.get('produto')?.setValue(this.produtoSelected);
        // Busca os dados completos do produto pelo ID antes de setar
        const produtoIdParaConsulta = this.data.produto.id || this.data.produto.codigo;
        this.produtoService
          .getProdutoById(produtoIdParaConsulta)
          .pipe(takeUntil(this._onDestroy))
          .subscribe(
            (prod) => {
              this.produtoSelected = prod;
              this.precoForm.get("produto")?.setValue(prod);

              // Garante que o produto esteja na lista e selecionado no select
              if (this.produtoSelected) {
                this.produtos = [prod];
                this.filteredProdutos.next(this.produtos);
              }

              this.loadProdutoDetails();
            },
            (error) => {
              // Se falhar, usa o produto parcial mesmo
              this.produtoSelected = this.data.produto;
              this.precoForm.get("produto")?.setValue(this.produtoSelected);

              this.produtos = [this.produtoSelected];
              this.filteredProdutos.next(this.produtos);

              this.loadProdutoDetails();
            }
          );
      }
    } else {
      console.log("ProdutoManutencaoPrecoComponent chamado pela rota");
      // Carregar produtos iniciais apenas se não foi pré-selecionado
      if (!this.produtoSelected) {
        this.produtoService.getProdutoPage(1, 10).subscribe((produtos) => {
          this.produtos = produtos.content;
          this.filteredProdutos.next(this.produtos);
        });
      } else {
        // Se já tem produto selecionado, adiciona à lista
        this.produtos = [this.produtoSelected];
        this.filteredProdutos.next(this.produtos);
      }
    }

    // Recuperar valores dos cookies e aplicar ao formulário
    this.loadValuesFromCookies();

    // Configurar subscriptions para recalcular valores
    this.precoForm.get('custoCompra')?.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      if (this.atualizandoCustoProgramaticamente) {
        return;
      }
      this.custoManualEditado = true;
      this.recalcularComBaseNoInputAtivo();
    });
    this.precoForm.get('precoVendaInput')?.valueChanges.pipe(debounceTime(300)).subscribe(value => this.recalcularPrecoVenda(value));
    this.precoForm.get('lucrosSPreInput')?.valueChanges.pipe(debounceTime(300)).subscribe(value => this.recalcularLucrosSPre(value));
    this.precoForm.get('lucrosSPrecoCustoInput')?.valueChanges.pipe(debounceTime(300)).subscribe(value => this.recalcularLucrosSPrecoCusto(value));
    this.precoForm.get('valorPrecoVendaInput')?.valueChanges.pipe(debounceTime(300)).subscribe(value => this.recalcularValorPrecoVenda(value));
    this.precoForm.get('valorPrecoCustoInput')?.valueChanges.pipe(debounceTime(300)).subscribe(value => this.recalcularValorPrecoCusto(value));
    this.precoForm.get('indiceInput')?.valueChanges.pipe(debounceTime(300)).subscribe(value => this.recalcularIndice(value));

    // Subscriptions para encargos com gravação em cookies
    this.precoForm.get('impostoSVenda')?.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      this.saveToCookies('impostoSVenda', value);
      this.recalcularTotalEncargos();
    });
    this.precoForm.get('acrescimoFinanceiro')?.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      this.saveToCookies('acrescimoFinanceiro', value);
      this.recalcularTotalEncargos();
    });
    this.precoForm.get('despesaOperacional')?.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      this.saveToCookies('despesaOperacional', value);
      this.recalcularTotalEncargos();
    });
    this.precoForm.get('outros')?.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      this.saveToCookies('outros', value);
      this.recalcularTotalEncargos();
    });
    this.precoForm.get('comissao')?.valueChanges.pipe(debounceTime(300)).subscribe(value => {
      this.saveToCookies('comissao', value);
      this.recalcularTotalEncargos();
    });

    

    this.precoForm.get('searchProduto')?.valueChanges
      .pipe(
        filter(search => !!search),
        tap(() => this.searching = true),
        takeUntil(this._onDestroy),
        debounceTime(200),
        map(search => {
          if (!this.produtos) return [];
          return this.produtoService.getProdutoPageSearch(1, 10, search).subscribe(produtos => {
            this.filteredProdutos.next(produtos.content);
            this.produtos = produtos.content;
          });
        }),
        delay(200),
        takeUntil(this._onDestroy)
      )
      .subscribe(
        () => {
          this.searching = false;
          if (this.produtoSelected && !this.produtos.some(p => p.id === this.produtoSelected?.id)) {
            this.produtos.push(this.produtoSelected);
            this.filteredProdutos.next(this.produtos);
          }
        },
        error => {
          this.searching = false;
          this.confirmService.confirm({
            message: `A T E N Ç Ã O !  ERRO PESQUISANDO PRODUTO ... ${error}`,
            error: true
          });
        }
      );

    // Recalcular encargos iniciais após carregar os valores dos cookies
    this.recalcularTotalEncargos();
  }

  private loadValuesFromCookies(): void {
    const campos = ['impostoSVenda', 'acrescimoFinanceiro', 'despesaOperacional', 'outros', 'comissao'];
    campos.forEach(campo => {
      const valor = this.cookieService.get(campo);
      if (valor) {
        const numero = Number(valor);
        if (!isNaN(numero)) {
          this.precoForm.get(campo)?.setValue(numero, { emitEvent: false });
        }
      }
    });
  }

  private saveToCookies(campo: string, valor: number | null): void {
    if (valor !== null && valor !== undefined) {
      this.cookieService.set(campo, valor.toString(), 365);
    }
  }

  onSelectionChangeProduto(event: MatSelectChange): void {
    if (event.value) {
      this.produtoSelected = event.value;
      this.precoForm.get('produto')?.setValue(this.produtoSelected);
      this.custoManualEditado = false;
      this.loadProdutoDetails();
    }
  }

  loadProdutoDetails(): void {
    if (this.produtoSelected) {
      if (!this.custoManualEditado) {
        this.atualizandoCustoProgramaticamente = true;
        this.precoForm.get('custoCompra')?.setValue(this.produtoSelected.custoReposicao || 0, { emitEvent: false });
        this.atualizandoCustoProgramaticamente = false;
      }
      this.precoVendaAtual = this.produtoSelected.preco || 0;
      this.saldo = this.produtoSelected.saldo || 0;
      this.recalcularTotalEncargos();
    }
  }

  restaurarCustoCadastro(): void {
    if (!this.produtoSelected) {
      return;
    }

    this.custoManualEditado = false;
    this.atualizandoCustoProgramaticamente = true;
    this.precoForm.get('custoCompra')?.setValue(this.produtoSelected.custoReposicao || 0, { emitEvent: false });
    this.atualizandoCustoProgramaticamente = false;
    this.recalcularComBaseNoInputAtivo();
  }

  recalcularTotalEncargos(): void {
    const valores = this.precoForm.value;
    this.totalEncargos = (
      (valores.impostoSVenda || 0) +
      (valores.acrescimoFinanceiro || 0) +
      (valores.despesaOperacional || 0) +
      (valores.outros || 0) +
      (valores.comissao || 0)
    );
    this.recalcularValores(null);
  }

  recalcularPrecoVenda(value: number): void {
    if (value > 0) {
      this.resetInputsExceto('precoVendaInput');
      this.recalcularValores('PRECO_VENDA_INPUT');
    }
  }

  recalcularLucrosSPre(value: number): void {
    if (value > 0) {
      this.resetInputsExceto('lucrosSPreInput');
      this.recalcularValores('LUCROS_S_PRECO_VENDA_INPUT');
    }
  }

  recalcularLucrosSPrecoCusto(value: number): void {
    if (value > 0) {
      this.resetInputsExceto('lucrosSPrecoCustoInput');
      this.recalcularValores('LUCRO_S_PRECO_CUSTO_INPUT');
    }
  }

  recalcularValorPrecoVenda(value: number): void {
    if (value > 0) {
      this.resetInputsExceto('valorPrecoVendaInput');
      this.recalcularValores('VALOR_PRECO_VENDA_INPUT');
    }
  }

  recalcularValorPrecoCusto(value: number): void {
    if (value > 0) {
      this.resetInputsExceto('valorPrecoCustoInput');
      this.recalcularValores('VALOR_PRECO_CUSTO_INPUT');
    }
  }

  recalcularIndice(value: number): void {
    if (value > 0) {
      this.resetInputsExceto('indiceInput');
      this.recalcularValores('INDICE_INPUT');
    }
  }

  private recalcularComBaseNoInputAtivo(): void {
    const valores = this.precoForm.value;

    if ((valores.precoVendaInput || 0) > 0) {
      this.recalcularValores('PRECO_VENDA_INPUT');
      return;
    }

    if ((valores.lucrosSPreInput || 0) > 0) {
      this.recalcularValores('LUCROS_S_PRECO_VENDA_INPUT');
      return;
    }

    if ((valores.lucrosSPrecoCustoInput || 0) > 0) {
      this.recalcularValores('LUCRO_S_PRECO_CUSTO_INPUT');
      return;
    }

    if ((valores.valorPrecoVendaInput || 0) > 0) {
      this.recalcularValores('VALOR_PRECO_VENDA_INPUT');
      return;
    }

    if ((valores.valorPrecoCustoInput || 0) > 0) {
      this.recalcularValores('VALOR_PRECO_CUSTO_INPUT');
      return;
    }

    if ((valores.indiceInput || 0) > 0) {
      this.recalcularValores('INDICE_INPUT');
      return;
    }

    this.recalcularValores(null);
  }

  resetInputsExceto(excecao: string): void {
    const campos = [
      'precoVendaInput',
      'lucrosSPreInput',
      'lucrosSPrecoCustoInput',
      'valorPrecoVendaInput',
      'valorPrecoCustoInput',
      'indiceInput'
    ];
    campos.forEach(campo => {
      if (campo !== excecao) {
        this.precoForm.get(campo)?.setValue(0, { emitEvent: false });
      }
    });
  }

  recalcularValores(campoParaCalculo: string | null): void {

    if (this.produtoSelected === undefined) {
      this.precoForm.get('produto')?.markAsTouched();
      this.precoForm.get('produto')?.setErrors({ required: true });
      return;
    }

    const valores = this.precoForm.value;
    const custo = valores.custoCompra || 0;
    const CEM = 100;

    if (custo <= 0) {
      this.confirmService.confirm({
        message: 'Não é possível calcular com o preço de custo zerado! Verifique!',
        error: true
      });
      return;
    }

    this.precoVenda = 0;
    this.totalEncargos = (
      (valores.impostoSVenda || 0) +
      (valores.acrescimoFinanceiro || 0) +
      (valores.despesaOperacional || 0) +
      (valores.outros || 0) +
      (valores.comissao || 0)
    );

    this.valorEncargo = 0;
    this.valorPrecoCusto = valores.valorPrecoCustoInput || 0;
    this.lucrosSPrecoCusto = valores.lucrosSPrecoCustoInput || 0;
    this.valorPrecoVenda = valores.valorPrecoVendaInput || 0;
    this.lucrosSPrecoVenda = valores.lucrosSPreInput || 0;

    if (campoParaCalculo === 'LUCROS_S_PRECO_VENDA_INPUT') {
      this.lucrosSPrecoVenda = valores.lucrosSPreInput || 0;
    }

    if (campoParaCalculo === 'LUCRO_S_PRECO_CUSTO_INPUT') {
      this.lucrosSPrecoCusto = valores.lucrosSPrecoCustoInput || 0;
      this.precoVenda = custo + (custo * (this.lucrosSPrecoCusto / CEM));
      this.valorEncargo = this.precoVenda * (this.totalEncargos / CEM);
      this.valorPrecoVenda = this.precoVenda - this.valorEncargo - custo;
      this.lucrosSPrecoVenda = (this.valorPrecoVenda / this.precoVenda) * CEM;
    }

    if (campoParaCalculo === 'VALOR_PRECO_VENDA_INPUT') {
      this.valorPrecoVenda = valores.valorPrecoVendaInput || 0;
      this.precoVenda = (custo + this.valorPrecoVenda) / (1 - (this.totalEncargos / CEM));
      this.valorEncargo = this.precoVenda * (this.totalEncargos / CEM);
      this.lucrosSPrecoVenda = (this.valorPrecoVenda / this.precoVenda) * CEM;
    }

    if (campoParaCalculo === 'VALOR_PRECO_CUSTO_INPUT') {
      this.valorPrecoCusto = valores.valorPrecoCustoInput || 0;
      this.precoVenda = custo + this.valorPrecoCusto;
      this.valorEncargo = this.precoVenda * (this.totalEncargos / CEM);
      this.valorPrecoVenda = this.precoVenda - this.valorEncargo - custo;
      this.lucrosSPrecoVenda = (this.valorPrecoVenda / this.precoVenda) * CEM;
    }

    if (campoParaCalculo !== 'PRECO_VENDA_INPUT') {
      if (this.lucrosSPrecoVenda >= CEM) {
        this.confirmService.confirm({
          message: 'Lucro sobre a venda não pode ser superior a 100%!',
          error: true
        });
        return;
      }
      this.precoVenda = custo / (1 - ((this.totalEncargos + this.lucrosSPrecoVenda) / CEM));
    }

    if (campoParaCalculo === 'PRECO_VENDA_INPUT') {
      this.precoVenda = valores.precoVendaInput || 0;
    }

    if (campoParaCalculo === 'INDICE_INPUT') {
      this.precoVenda = custo * (valores.indiceInput || 0);
    }

    this.valorEncargo = this.precoVenda * (this.totalEncargos / CEM);
    this.valorPrecoVenda = this.precoVenda - this.valorEncargo - custo;
    this.valorPrecoCusto = this.precoVenda - custo;
    this.lucrosSPrecoVenda = (this.valorPrecoVenda / this.precoVenda) * CEM;

    if (campoParaCalculo !== 'LUCRO_S_PRECO_CUSTO_INPUT' && custo > 0) {
      this.lucrosSPrecoCusto = ((this.precoVenda / custo) * CEM) - CEM;
    }

    this.totalCustoVenda = CEM - this.lucrosSPrecoVenda;
    this.valorCustoVenda = custo + this.valorEncargo;
    this.indiceCalc = custo > 0 ? this.precoVenda / custo : 0;

    this.atualizarValoresComponente();
  }

  atualizarValoresComponente(): void {
    this.precoVenda = Number(this.precoVenda.toFixed(6));
    this.valorEncargo = Number(this.valorEncargo.toFixed(6));
    this.valorPrecoVenda = Number(this.valorPrecoVenda.toFixed(6));
    this.lucrosSPrecoVenda = Number(this.lucrosSPrecoVenda.toFixed(6));
    this.valorPrecoCusto = Number(this.valorPrecoCusto.toFixed(6));
    this.lucrosSPrecoCusto = Number(this.lucrosSPrecoCusto.toFixed(6));
    this.totalCustoVenda = Number(this.totalCustoVenda.toFixed(6));
    this.valorCustoVenda = Number(this.valorCustoVenda.toFixed(6));
    this.indiceCalc = Number(this.indiceCalc.toFixed(6));
  }

  gravarPrecoVendaSugerido(): void {
    if (!this.produtoSelected) {
      this.confirmService.confirm({
        message: 'Selecione um produto antes de gravar o preço!',
        error: true
      });
      return;
    }

    if (this.precoVenda <= 0) {
      this.confirmService.confirm({
        message: 'O preço de venda sugerido deve ser maior que zero!',
        error: true
      });
      return;
    }

    // Validação adicional: preço sugerido não pode ser menor que o custo
    const custoCompra = this.precoForm.get('custoCompra')?.value || 0;
    if (this.precoVenda < custoCompra) {
      this.confirmService.confirm({
        message: 'O preço de venda sugerido não pode ser inferior ao custo de compra!',
        error: true
      });
      return;
    }

    this.loader.open();

    // Busca os preços atuais do produto
    this.produtoService.getPrecos(this.produtoSelected.id).subscribe(
      (precos: Preco[]) => {
        let filialId = this.jwtAuth?.item?.empresaRemota !== undefined && this.jwtAuth?.item?.empresaRemota !== null ? this.jwtAuth?.item?.empresaRemota?.filialApi : this.jwtAuth?.filial?.id;
        this.parametroService.getParametros(filialId).subscribe(
          (parametros) => {
            let precoTabela1 = precos.find(p => p.listaId === parametros.fatListaPrecoPadrao && p.tabelaId === parametros.fatTabelaVendaPadrao)
            console.log('Preço encontrado na tabela 1:', precoTabela1);
            if (precoTabela1) {
              // Atualiza o preço existente na tabela 1
              precoTabela1.preco = this.precoVenda;
              precoTabela1.listaId = precoTabela1.listaId || parametros.fatListaPrecoPadrao;
              precoTabela1.tabelaId = precoTabela1.tabelaId || parametros.fatTabelaVendaPadrao;
            } else {
              // Cria um novo preço para a tabela 1
              const novoPreco: Preco = {
                produtoId: this.produtoSelected!.id,
                listaId: parametros.fatListaPrecoPadrao, // Lista padrão
                tabelaId: parametros.fatTabelaVendaPadrao, // Tabela Padrao
                preco: this.precoVenda,
                listaPreco: parametros.listaPrecoPadrao,
                tabela: parametros.listaPrecoTabela,
                precoPromocao: 0, // Default value
                saldo: this.produtoSelected?.saldo ?? 0, // Default value
                tabelaDesc: '', // Default value
                listaDesc: '', // Default value
                validadePromocao: undefined, // Default value
                percentualAcrescimo: 0, // Default value
                percentualDesc: 0, // Default value
                observacao: undefined, // Default value
                produtoUuid: this.produtoSelected!.uuid
              };
              precos.push(novoPreco);
            }

            // Salva os preços atualizados
            this.produtoService.saveOrUpdatePrecos(precos).subscribe(
              (precosAtualizados: Preco[]) => {
                this.precoVendaAtual = this.precoVenda;
                this.loader.close();

                this.confirmService.confirm({
                  message: `Preço de venda (${this.precoVenda.toFixed(2)}) atualizado com sucesso na tabela 1 para o produto ${this.produtoSelected!.codigo} - ${this.produtoSelected!.descricao}!`,
                  error: false
                });

                // Atualiza o produtoSelected com o novo preço
                this.produtoSelected!.preco = this.precoVenda;
                this.loadProdutoDetails();

                // Se foi chamado como popup, pode fechar após salvar
                if (this.isPopup()) {
                  setTimeout(() => {
                    this.close();
                  }, 2000); // Fecha após 2 segundos para permitir visualizar a mensagem
                }
              },
              (error) => {
                this.loader.close();
                this.confirmService.confirm({
                  message: `Erro ao atualizar o preço na tabela 1: ${error.message || error}`,
                  error: true
                });
              }
            );
          },
          (error) => {
            this.loader.close();
            this.confirmService.confirm({
              message: `Erro ao buscar os parâmetros: ${error.message || error}`,
              error: true
            });
          }
        );
      },
      (error) => {
        this.loader.close();
        this.confirmService.confirm({
          message: `Erro ao buscar os preços do produto: ${error.message || error}`,
          error: true
        });
      }
    );
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  /**
   * Verifica se foi chamado como popup
   */
  isPopup(): boolean {
    return !!(this.data && this.dialogRef);
  }

  /**
   * Fecha o dialog ou navega de volta
   */
  close() {
    if (this.isPopup()) {
      // Chamado como popup - retorna o produto atualizado
      this.dialogRef.close({
        produto: this.produtoSelected,
        precoAtualizado: this.precoVenda
      });
    } else {
      // Chamado pela rota
      this.goBack();
    }
  }

  /**
   * Navega de volta ou fecha o popup
   */
  goBack() {
    if (this.isPopup()) {
      // Se foi chamado como popup, fecha o dialog
      this.dialogRef.close();
    } else {
      // Se foi chamado pela rota, navega de volta
      this.router.navigate(['/dashboard/main']);
    }
  }
}